import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from './app-settings.service';
import { GisVendorAdapter } from './gis/gis-vendor-adapter';
import { resolveGisAdapter } from './gis/gisoft-gis.adapter';
import { GisAddressInfo, GisEntityInfo, GisEntityName, GisError } from './gis/gis.models';

/** Haritanın gösterileceği yer tutucu (dk-gis-map içindeki boş div). */
interface GisSlot {
  id: number;
  el: HTMLElement;
  entityName: GisEntityName | null;
  entityId: number | null;
}

/** Harita uygulaması initializeComplete göndermezse hata bildirmek için bekleme süresi. */
const READY_TIMEOUT_MS = 30000;
/** Sahip slot yokken görünürlük kontrolü kaç karede bir yapılır. */
const IDLE_CHECK_EVERY_FRAMES = 6;
/** Kırpan ata elemanlar / z-index kaç karede bir yeniden hesaplanır. */
const GEOMETRY_REFRESH_FRAMES = 30;
/** Slotun başka bir eleman (ör. formun detay paneli) tarafından örtülmesi kaç karede bir kontrol edilir. */
const OCCLUSION_CHECK_FRAMES = 6;
/** Örtülme için yoklanan noktalar (slot içinde oransal konum). */
const OCCLUSION_POINTS: ReadonlyArray<[number, number]> = [[0.5, 0.5], [0.2, 0.2], [0.8, 0.2], [0.2, 0.8], [0.8, 0.8]];
/** Yan paneldeki haritanın sabitlenme (pin) tercihi. */
const PIN_STORAGE_KEY = 'dk-gis-harita-sabit';

/**
 * Uygulama genelinde TEK harita iframe'ini yönetir.
 *
 * iframe DOM'da taşındığında tarayıcı onu yeniden yükler; bu yüzden iframe body'ye bir kez eklenir
 * ve hiç taşınmaz. Ekrandaki her `dk-gis-map` yalnızca bir yer tutucu (slot) kaydeder; servis iframe'i
 * o an görünür olan en son slotun üzerine `position: fixed` ile konumlandırır. Görünür slot yoksa
 * iframe ekran dışına park edilir ve yüklü kalır — sekme/form değişince harita yeniden yüklenmez.
 *
 * Haberleşme vendor adapter'ı (app.settings `gisVendor`) üzerinden postMessage ile yapılır.
 */
@Injectable({
  providedIn: 'root'
})
export class GisMapService implements OnDestroy {
  /** Harita uygulaması yüklenip mesaj almaya hazır mı (initializeComplete). */
  readonly ready$ = new BehaviorSubject<boolean>(false);
  /** Haritada seçilen nesnenin bilgisi ("Ybs'de göster"). */
  readonly entityInfo$ = new Subject<GisEntityInfo>();
  /** Haritada tıklanan noktanın adres/koordinat bilgisi. */
  readonly addressInfo$ = new Subject<GisAddressInfo>();
  readonly error$ = new Subject<GisError>();
  /** iframe'in o an üzerinde durduğu slot (yoksa null). */
  readonly activeSlotId$ = new BehaviorSubject<number | null>(null);
  /** Aktif slot sayfadaki başka bir eleman tarafından örtülüyor mu (o sırada harita gizlenir). */
  readonly occluded$ = new BehaviorSubject<boolean>(false);
  /** Yan panel modundaki haritalar sayfada sabit mi (true) yoksa kenardan açılır panel mi (false). Tüm formlarda ortak. */
  readonly pinned$ = new BehaviorSubject<boolean>(this.readPinned());

  private adapter: GisVendorAdapter | null = null;
  private host?: HTMLDivElement;
  private iframe?: HTMLIFrameElement;
  private targetOrigin = '*';
  private loadCount = 0;
  private readyTimer: ReturnType<typeof setTimeout> | null = null;

  private slots: GisSlot[] = [];
  private slotSeq = 0;
  private owner: GisSlot | null = null;
  private lastShownKey: string | null = null;

  private rafId: number | null = null;
  private frame = 0;
  private clipAncestors: HTMLElement[] = [];
  private zIndex = 1;
  private lastStyleKey = '';
  private occluded = false;

  private readonly onWindowMessage = (ev: MessageEvent) => this.handleMessage(ev);

  constructor(private zone: NgZone, @Inject(DOCUMENT) private document: Document) {}

  /** app.settings `gisVendor` desteklenen bir vendor mu (değilse "Lisanslanmamış"). */
  get licensed(): boolean {
    return !!resolveGisAdapter(environment.gisVendor);
  }

  /** Lisanslı ve `gisUrl` tanımlı mı. */
  get configured(): boolean {
    return this.licensed && !!this.gisUrl;
  }

  private get gisUrl(): string {
    return (environment.gisUrl ?? '').trim();
  }

  /** Yan panel modundaki haritaların sabitlenme tercihini değiştirir (tarayıcıda hatırlanır). */
  setPinned(pinned: boolean): void {
    try {
      localStorage.setItem(PIN_STORAGE_KEY, pinned ? '1' : '0');
    } catch {
      // tarayıcı depolaması kapalı olabilir; tercih yalnızca bu oturumda geçerli olur
    }
    this.pinned$.next(pinned);
  }

  private readPinned(): boolean {
    try {
      return localStorage.getItem(PIN_STORAGE_KEY) !== '0';
    } catch {
      return true;
    }
  }

  /** Haritayı, ilk ihtiyaç beklenmeden arka planda yükler (app.settings `gisPreload`). */
  preload(): void {
    this.ensureHost();
  }

  /** Bir yer tutucu kaydeder; harita yapılandırılmamışsa null döner. */
  attach(el: HTMLElement, entityName: GisEntityName | null, entityId: number | null): number | null {
    if (!this.ensureHost()) return null;
    const slot: GisSlot = { id: ++this.slotSeq, el, entityName, entityId };
    this.slots.push(slot);
    this.startLoop();
    return slot.id;
  }

  /** Slotun göstereceği nesneyi değiştirir; slot o an sahipse harita hemen güncellenir. */
  update(slotId: number, entityName: GisEntityName | null, entityId: number | null): void {
    const slot = this.slots.find(s => s.id === slotId);
    if (!slot) return;
    slot.entityName = entityName;
    slot.entityId = entityId;
    if (slot === this.owner) this.showOwnerEntity();
  }

  detach(slotId: number): void {
    this.slots = this.slots.filter(s => s.id !== slotId);
    if (this.owner?.id === slotId) this.setOwner(null);
    if (!this.slots.length) {
      this.stopLoop();
      this.park();
    }
  }

  ngOnDestroy(): void {
    this.stopLoop();
    this.clearReadyTimer();
    window.removeEventListener('message', this.onWindowMessage);
    this.host?.remove();
  }

  // ---------------------------------------------------------------- iframe

  private ensureHost(): boolean {
    if (this.host) return true;
    this.adapter = resolveGisAdapter(environment.gisVendor);
    if (!this.adapter || !this.gisUrl) return false;

    let url: URL;
    try {
      url = new URL(this.gisUrl, this.document.baseURI);
    } catch {
      console.error('[GIS] app.settings gisUrl geçersiz:', this.gisUrl);
      return false;
    }
    this.targetOrigin = url.origin && url.origin !== 'null' ? url.origin : '*';

    this.zone.runOutsideAngular(() => {
      const host = this.document.createElement('div');
      host.className = 'dk-gis-host';
      Object.assign(host.style, {
        position: 'fixed',
        overflow: 'hidden',
        margin: '0',
        padding: '0',
        border: '0',
        background: '#fff',
      });

      const iframe = this.document.createElement('iframe');
      iframe.title = 'Harita';
      iframe.setAttribute('allow', 'geolocation; fullscreen; clipboard-write');
      Object.assign(iframe.style, { width: '100%', height: '100%', border: '0', display: 'block' });
      iframe.addEventListener('load', () => this.onIframeLoad());
      iframe.src = url.href;

      window.addEventListener('message', this.onWindowMessage);
      host.appendChild(iframe);
      this.host = host;
      this.iframe = iframe;
      this.park();
      this.document.body.appendChild(host);
    });
    return true;
  }

  private onIframeLoad(): void {
    // İlk yüklemeden sonraki load'lar iframe'in kendi içinde tamamen yeniden yüklendiği anlamına gelir.
    if (this.loadCount++ > 0 && this.ready$.value) {
      this.lastShownKey = null;
      this.zone.run(() => this.ready$.next(false));
    }
    this.clearReadyTimer();
    if (!this.ready$.value) {
      this.readyTimer = setTimeout(() => {
        if (this.ready$.value) return;
        this.zone.run(() =>
          this.error$.next({ errorCode: 'TIMEOUT', errorMessage: 'Harita uygulaması yanıt vermedi.' })
        );
      }, READY_TIMEOUT_MS);
    }
  }

  private clearReadyTimer(): void {
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
  }

  private handleMessage(ev: MessageEvent): void {
    if (!this.iframe || !this.adapter || ev.source !== this.iframe.contentWindow) return;
    if (this.targetOrigin !== '*' && ev.origin !== this.targetOrigin) return;

    const event = this.adapter.parseInbound(ev.data);
    if (!event) return;
    console.debug('[GIS] ←', ev.data);

    switch (event.type) {
      case 'ready':
        this.clearReadyTimer();
        this.lastShownKey = null;
        this.zone.run(() => this.ready$.next(true));
        this.showOwnerEntity();
        break;
      case 'entityInfo':
        this.zone.run(() => this.entityInfo$.next(event.info));
        break;
      case 'addressInfo':
        this.zone.run(() => this.addressInfo$.next(event.info));
        break;
      case 'error':
        this.zone.run(() => this.error$.next(event.error));
        break;
      case 'unknown':
        console.warn('[GIS] Harita uygulamasından bilinmeyen mesaj:', event.name);
        break;
    }
  }

  private showOwnerEntity(): void {
    const slot = this.owner;
    const target = this.iframe?.contentWindow;
    if (!slot || !slot.entityName || slot.entityId == null || !this.ready$.value || !target || !this.adapter) return;

    const key = `${slot.entityName}:${slot.entityId}`;
    if (key === this.lastShownKey) return;
    const message = this.adapter.buildShowEntityMessage(slot.entityName, slot.entityId);
    console.debug('[GIS] →', message);
    target.postMessage(message, this.targetOrigin);
    this.lastShownKey = key;
  }

  // ---------------------------------------------------------------- konumlandırma

  private setOwner(slot: GisSlot | null): void {
    if (slot === this.owner) return;
    this.owner = slot;
    this.lastShownKey = null;
    this.lastStyleKey = '';
    if (slot) this.refreshGeometry(slot.el);
    this.setOccluded(false);
    this.zone.run(() => this.activeSlotId$.next(slot?.id ?? null));
    this.showOwnerEntity();
  }

  private startLoop(): void {
    if (this.rafId != null) return;
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(this.tick);
    });
  }

  private stopLoop(): void {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  private readonly tick = (): void => {
    this.rafId = null;
    if (!this.slots.length) {
      this.park();
      return;
    }
    this.frame++;
    if (this.owner || this.frame % IDLE_CHECK_EVERY_FRAMES === 0) {
      const owner = this.pickOwner();
      this.setOwner(owner);
      if (owner) this.place(owner);
      else this.park();
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  /** Görünür slotlardan en son kaydolanı. */
  private pickOwner(): GisSlot | null {
    for (let i = this.slots.length - 1; i >= 0; i--) {
      if (this.isVisible(this.slots[i].el)) return this.slots[i];
    }
    return null;
  }

  private isVisible(el: HTMLElement): boolean {
    if (!el.isConnected) return false;
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return false;
    if (r.right <= 0 || r.bottom <= 0 || r.left >= window.innerWidth || r.top >= window.innerHeight) return false;
    const check = (el as any).checkVisibility;
    return typeof check === 'function' ? check.call(el, { visibilityProperty: true }) : true;
  }

  /**
   * Taşmayı kırpan ataları (scroll alanları) ve z-index'i hesaplar.
   * z-index: kök seviyede yarışan en dıştaki z-index'li ata (ör. DevExtreme popup wrapper) + 1;
   * böylece popup içindeki harita popup'ın üstünde, sonradan açılan alert/dialog'ların altında kalır.
   */
  private refreshGeometry(el: HTMLElement): void {
    const clip: HTMLElement[] = [];
    for (let a = el.parentElement; a && a !== this.document.body; a = a.parentElement) {
      const cs = getComputedStyle(a);
      if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') clip.push(a);
    }
    this.clipAncestors = clip;
    this.zIndex = this.rootStacking(el).z + 1;
  }

  private place(slot: GisSlot): void {
    if (!this.host) return;
    if (this.frame % GEOMETRY_REFRESH_FRAMES === 0) this.refreshGeometry(slot.el);

    const r = slot.el.getBoundingClientRect();
    let top = Math.max(r.top, 0);
    let left = Math.max(r.left, 0);
    let right = Math.min(r.right, window.innerWidth);
    let bottom = Math.min(r.bottom, window.innerHeight);
    for (const a of this.clipAncestors) {
      const ar = a.getBoundingClientRect();
      top = Math.max(top, ar.top);
      left = Math.max(left, ar.left);
      right = Math.min(right, ar.right);
      bottom = Math.min(bottom, ar.bottom);
    }
    if (this.frame % OCCLUSION_CHECK_FRAMES === 0) this.setOccluded(this.isOccluded(slot.el, r));
    const visible = right > left && bottom > top && !this.occluded;
    const clipPath = visible ? `inset(${top - r.top}px ${r.right - right}px ${r.bottom - bottom}px ${left - r.left}px)` : 'none';

    const key = `${r.left}|${r.top}|${r.width}|${r.height}|${clipPath}|${this.zIndex}|${visible}`;
    if (key === this.lastStyleKey) return;
    this.lastStyleKey = key;

    Object.assign(this.host.style, {
      left: `${r.left}px`,
      top: `${r.top}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
      zIndex: String(this.zIndex),
      clipPath,
      visibility: visible ? 'visible' : 'hidden',
      pointerEvents: visible ? 'auto' : 'none',
    });
    this.host.removeAttribute('aria-hidden');
  }

  /**
   * Slotun önünde, haritanın altında kalacak bir eleman var mı (ör. formun açılan detay paneli).
   * Her noktada eleman yığınında haritanın zaten üstünde çizilenler (kök seviyedeki z-index'i daha büyük olan
   * popup, açılır liste, diyalog gölgesi vb.) atlanır; kalan ilk eleman slotun kendisi değilse o nokta örtülüdür.
   * Ortadaki nokta ya da beş noktanın çoğu örtülüyse slot örtülü kabul edilir.
   */
  private isOccluded(el: HTMLElement, r: DOMRect): boolean {
    if (!this.host || typeof this.document.elementsFromPoint !== 'function') return false;
    const host = this.host;
    const kokZCache = new Map<Element, { z: number; kok: Element | null }>();
    const haritaninUstunde = (e: Element): boolean => {
      let k = kokZCache.get(e);
      if (!k) {
        k = this.rootStacking(e);
        kokZCache.set(e, k);
      }
      if (k.z !== this.zIndex) return k.z > this.zIndex;
      // eşit z-index'te DOM'da sonra gelen üstte çizilir
      return !!k.kok && !!(host.compareDocumentPosition(k.kok) & Node.DOCUMENT_POSITION_FOLLOWING);
    };
    let ortulen = 0;
    let ortaOrtulu = false;
    OCCLUSION_POINTS.forEach(([fx, fy], i) => {
      const x = r.left + r.width * fx;
      const y = r.top + r.height * fy;
      if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return;
      const alttaki = this.document.elementsFromPoint(x, y).find(e => !host.contains(e) && !haritaninUstunde(e));
      const ortulu = !!alttaki && alttaki !== el && !el.contains(alttaki);
      if (ortulu) {
        ortulen++;
        if (i === 0) ortaOrtulu = true;
      }
    });
    return ortaOrtulu || ortulen >= 3;
  }

  /**
   * Elemanın sayfa kökünde yarıştığı katman: en dıştaki katman bağlamı (stacking context) atası ve onun z-index'i.
   * İçerideki z-index'ler (ör. transform'lu bir panelin içindeki 9999) kökte geçersizdir; bu yüzden en dıştaki alınır.
   */
  private rootStacking(e: Element): { z: number; kok: Element | null } {
    let kok: Element | null = null;
    let kokStil: CSSStyleDeclaration | null = null;
    for (let a: Element | null = e; a && a !== this.document.body; a = a.parentElement) {
      const cs = getComputedStyle(a);
      if (this.createsStackingContext(a, cs)) {
        kok = a;
        kokStil = cs;
      }
    }
    const z = kokStil && kokStil.zIndex !== 'auto' ? parseInt(kokStil.zIndex, 10) : 0;
    return { z: isNaN(z) ? 0 : z, kok };
  }

  /** CSS kurallarına göre eleman kendi katman bağlamını (stacking context) oluşturuyor mu. */
  private createsStackingContext(el: Element, cs: CSSStyleDeclaration): boolean {
    if (cs.position === 'fixed' || cs.position === 'sticky') return true;
    if (cs.zIndex !== 'auto') {
      if (cs.position !== 'static') return true;
      const ebeveyn = el.parentElement ? getComputedStyle(el.parentElement).display : '';
      if (/flex|grid/.test(ebeveyn)) return true;
    }
    return parseFloat(cs.opacity) < 1
      || (!!cs.transform && cs.transform !== 'none')
      || (!!cs.filter && cs.filter !== 'none')
      || (!!cs.perspective && cs.perspective !== 'none')
      || (!!cs.clipPath && cs.clipPath !== 'none')
      || (!!cs.mixBlendMode && cs.mixBlendMode !== 'normal')
      || cs.isolation === 'isolate'
      || /transform|opacity|filter|perspective/.test(cs.willChange || '')
      || /paint|layout|strict|content/.test(cs.contain || '');
  }

  private setOccluded(value: boolean): void {
    if (value === this.occluded) return;
    this.occluded = value;
    this.lastStyleKey = '';
    this.zone.run(() => this.occluded$.next(value));
  }

  /** Ekran dışında ama gerçek boyutta tutar: harita motoru 0 boyutla düzgün başlamaz. */
  private park(): void {
    if (!this.host || this.lastStyleKey === 'parked') return;
    this.lastStyleKey = 'parked';
    Object.assign(this.host.style, {
      left: '-20000px',
      top: '0px',
      width: '1024px',
      height: '768px',
      zIndex: '-1',
      clipPath: 'none',
      visibility: 'hidden',
      pointerEvents: 'none',
    });
    this.host.setAttribute('aria-hidden', 'true');
  }
}
